import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import LocalizedDataGrid from "../../ui/datagrid/LocalizedDataGrid";
import Fab from "@mui/material/Fab";
import AddIcon from "@mui/icons-material/Add";
import { ModalMaterial } from "../../modals/Material";
import * as React from "react";
import { useSelector } from "react-redux";
import LinearProgress from "@mui/material/LinearProgress";
import { NoRowOverlay } from "../../table/NoRowOverlay";
import { deleteMaterial, editMaterial } from "../../../redux/actions/material";
import {
  getMaterials,
  getMaterialsByCenter,
} from "../../../redux/actions/material";
import { store } from "../../../store";
import { useLocation, useNavigate } from "react-router-dom";
import CSVDownloader from "../../ui/CSVDownloader";
import { getToday } from "../../ui/utils/DateUtils";
import { Button } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import {
  addMaterialToProtocol,
  getCenterProtocols,
  getProtocols,
} from "../../../services/protocolService";
import {
  showErrorAlert,
  showSuccessAlert,
  showWarningAlert,
} from "../../ui/utils/AlertUtils";
import { useTranslation } from "react-i18next";
import EditMaterialDialog from "../../ui/dialogs/EditMaterialDialog";
import ProtocolSelectorDialog from "../../ui/dialogs/ProtocolSelectorDialog";

const SPACING = 50;

export const ScreenMaterials = (props) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const studyCode = location.state?.studyCode;
  const centerNumber = location.state?.centerNumber;
  const user = useSelector((state) => state.user.user);
  const adminUser = user.role === "admin";
  const centerUser = user.organization === "Center";
  const isLoading = useSelector((state) => state.material.isLoadingMaterials);
  const studyId = location.state?.studyId;
  const centerId = location.state?.centerId;
  const [visibleCreateEdit, setVisibleCreateEdit] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [selectedCell, setSelectedCell] = React.useState(null);
  const [name, setName] = React.useState("");
  const [type, setType] = React.useState("");
  const [count, setCount] = React.useState("");
  const [status, setStatus] = React.useState("");
  const [selectedRows, setSelectedRows] = React.useState([]);
  const [showProtocolSelector, setShowProtocolSelector] = React.useState(false);
  const [protocols, setProtocols] = React.useState([]);
  const [centerProtocols, setCenterProtocols] = React.useState([]);
  const [selectedProtocol, setSelectedProtocol] = React.useState("");
  // Add state to hold the row being edited
  const [editMaterialData, setEditMaterialData] = React.useState(null);

  // -- Load materials from the store
  const materials = useSelector((state) => state.material.materials);

  const updatedMaterialState = useSelector(
    (state) => state.material.updatedMaterial,
  );
  const deletedMaterial = useSelector(
    (state) => state.material.deletedMaterial,
  );
  const [updatedProtocolMaterials, setUpdatedProtocolMaterials] =
    React.useState(false);

  const handleAddMaterialToProtocol = async (protocolId) => {
    const materialIds = selectedRows;
    materialIds.forEach((materialId) => {
      if (materialId) {
        addMaterialToProtocol(protocolId, materialId);
      }
    });
  };

  const handleProtocolSelect = (event) => {
    const protocolId = event.target.value;
    setSelectedProtocol(protocolId);
  };

  const handleConfirm = () => {
    if (selectedProtocol) {
      handleAddMaterialToProtocol(selectedProtocol);
      setUpdatedProtocolMaterials(true);
      setShowProtocolSelector(false);
      showSuccessAlert(t("materialsAddedToProtocol"));
    }
  };

  React.useEffect(() => {
    if (updatedProtocolMaterials) {
      setUpdatedProtocolMaterials(false);
    }
  }, [updatedProtocolMaterials]);

  const handleCellDoubleClick = (params) => {
    if (params.field === "name") {
      setSelectedCell(params.id);
      const selectedMaterial = materials.find(
        (material) => material.id === params.id,
      );
      setName(selectedMaterial.name);
      setType(selectedMaterial.type);
      setStatus(selectedMaterial.status);
      setCount(selectedMaterial.totalCount);
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleDeleteMaterial = async () => {
    setOpen(false);
    try {
      const result = await showWarningAlert(t("deleteMaterialConfirmation"));
      if (result.isConfirmed) {
        await store.dispatch(deleteMaterial(selectedCell));
        showSuccessAlert(t("materialDeleted"));
      }
    } catch (error) {
      console.error(error);
      showErrorAlert(t("materialDeleteFailed"));
    }
  };

  const handleEdit = async (event) => {
    event.preventDefault();
    const updatedMaterial = {
      name: name,
      type: type,
      status: status,
    };
    setOpen(false);
    try {
      const response = await store.dispatch(
        editMaterial(selectedCell, updatedMaterial),
      );
      if (response) {
        showSuccessAlert(t("materialUpdated"));
      } else {
        showErrorAlert(t("materialUpdateFailed"));
      }
    } catch (error) {
      console.error(error);
      showErrorAlert(t("materialUpdateFailed"));
    }
  };

  // Prepare and open Material modal in edit mode
  const onEdit = (row) => {
    const protocolList = row.Protocols || row.protocols || [];
    const protocolIds = Array.isArray(protocolList)
      ? protocolList.map((p) => p.id)
      : [];
    setEditMaterialData({
      id: row.id,
      name: row.name,
      type: row.type,
      status: row.status,
      unitPrice: row.unitPrice ?? row.unit_price ?? "",
      protocolIds,
    });
    setVisibleCreateEdit(true);
  };

  const sortedMaterials = Array.isArray(materials)
    ? [...materials]
        .filter((material) => material.type === "material")
        .sort((a, b) => {
          const idA = a.Protocols?.[0]?.id ?? 0;
          const idB = b.Protocols?.[0]?.id ?? 0;
          if (idA !== idB) {
            return idB - idA;
          }

          // Extract numerical part from the name
          const extractNumber = (name) => {
            const match = name.match(/(\d+)/);
            return match ? parseInt(match[0], 10) : 0;
          };

          const numA = extractNumber(a.name);
          const numB = extractNumber(b.name);

          if (numA !== numB) {
            return numA - numB;
          }

          return a.name.localeCompare(b.name);
        })
        .concat(materials.filter((material) => material.type !== "material"))
    : [];

  const columns = React.useMemo(() => {
    const editColumn = {
      field: "edit",
      headerName: "",
      width: 50,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton
          size="small"
          color="primary"
          aria-label={t("edit")}
          onClick={() => onEdit(params.row)}>
          <EditIcon fontSize="small" />
        </IconButton>
      ),
    };

    if (centerId) {
      const cols = [
        { field: "name", headerName: t("material"), width: 500 },
        // {
        //   field: "unitPrice",
        //   headerName: t("unitPrice"),
        //   width: 140,
        // },
        {
          field: "productionCount",
          headerName: t("stock"),
          width: 100,
        },
        {
          field: "notes",
          headerName: "",
          width: 400,
          renderCell: (params) => (
            <div
              style={{ cursor: "pointer", textDecoration: "none" }}
              onClick={() =>
                navigate(`/productions`, {
                  state: {
                    centerId: centerId,
                    centerNumber: centerNumber,
                    studyId: studyId,
                    studyCode: studyCode,
                    materialId: params.row.id,
                    expiring: true,
                  },
                })
              }>
              {params.value}
            </div>
          ),
        },
        {
          field: "protocols",
          headerName: t("protocol"),
          width: 200,
          renderCell: (params) => {
            if (params.value) {
              return params.value
                .map((protocol) => protocol.reference)
                .join(", ");
            } else {
              return "";
            }
          },
        },
      ];
      return adminUser ? [editColumn, ...cols] : cols;
    } else {
      if (!centerUser) {
        const cols = [
          { field: "name", headerName: t("materialType"), width: 500 },
          // { field: "unitPrice", headerName: t("unitPrice"), width: 140 },
          { field: "availableCount", headerName: t("available"), width: 100 },
          { field: "inTransitCount", headerName: t("inTransit"), width: 100 },
          {
            field: "atCenterCount",
            headerName: t("atCenter"),
            width: 100,
          },
          {
            field: "attributedCount",
            headerName: t("attributed"),
            width: 100,
            valueGetter: (params) =>
              params.row.type === "material" ? params.value : "NA",
          },
          {
            field: "destructionCount",
            headerName: t("destroyed"),
            width: 100,
          },
          { field: "totalCount", headerName: t("total"), width: 100 },
          {
            field: "Protocols",
            headerName: t("usedInProtocols"),
            width: 200,
            renderCell: (params) => {
              if (params.value) {
                return params.value
                  .map((protocol) => protocol.reference)
                  .join(", ");
              } else {
                return "Loading...";
              }
            },
          },
          {
            field: "status",
            headerName: t("status"),
            width: 100,
          },
        ];
        return adminUser ? [editColumn, ...cols] : cols;
      } else {
        const cols = [{ field: "name", headerName: t("material"), width: 500 }];
        return adminUser ? [editColumn, ...cols] : cols;
      }
    }
  }, [
    centerId,
    centerUser,
    adminUser,
    centerNumber,
    studyId,
    studyCode,
    navigate,
    t,
  ]);

  // Extract the field values from the columns array
  const visibleColumns = columns.map((column) => column.field);
  const headerMapping = columns.reduce((acc, column) => {
    acc[column.field] = column.headerName;
    return acc;
  }, {});
  headerMapping["Protocols"] = t("usedInProtocols");

  // Filter the data based on the visible columns and replace keys with headerName
  const materialsToDownload =
    selectedRows.length > 0
      ? sortedMaterials.filter((material) => selectedRows.includes(material.id))
      : sortedMaterials;

  const filteredMaterials = materialsToDownload.map((row) => {
    const filteredRow = {};
    for (const key in row) {
      if (key === "protocols" || key === "Protocols") {
        // Map over the array and join the elements into a string
        filteredRow[headerMapping[key]] = row[key]
          .map((protocol) => protocol.reference)
          .join(", ");
      } else if (visibleColumns.includes(key)) {
        filteredRow[headerMapping[key]] = row[key];
      }
    }
    return filteredRow;
  });

  // Fetch materials when a material is updated
  React.useEffect(() => {
    const fetchData = async () => {
      if (centerId) {
        await store.dispatch(getMaterialsByCenter(centerId));
      } else if (studyId) {
        await store.dispatch(getMaterials(studyId));
      }
    };

    if (!materials || materials.length === 0) {
      fetchData();
    }
  }, [
    updatedMaterialState,
    updatedProtocolMaterials,
    deletedMaterial,
    centerId,
    studyId,
  ]);

  // Fetch materials
  React.useEffect(() => {
    const fetchData = async () => {
      if (centerId) {
        await store.dispatch(getMaterialsByCenter(centerId));
      } else if (studyId) {
        await store.dispatch(getMaterials(studyId));
      } else {
        navigate("/studies");
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studyId, centerId]);

  // Fetch protocols
  // ----------------------------------------------
  React.useEffect(() => {
    if (showProtocolSelector) {
      getProtocols(studyId).then((response) => {
        setProtocols(response);
      });
    }
  }, [showProtocolSelector, studyId]);

  // Fetch center protocols
  React.useEffect(() => {
    if (centerId) {
      getCenterProtocols(centerId).then((response) => {
        setCenterProtocols(response);
      });
    }
  }, [centerId]);

  // Set the selected protocol to the first protocol in the list by default
  React.useEffect(() => {
    if (protocols.length > 0) {
      setSelectedProtocol(protocols[0].id);
    } else {
      setSelectedProtocol("");
    }
  }, [protocols]);

  // ==============================================================================
  // Render
  // ==============================================================================
  return (
    <Grid item xs={12}>
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
        }}>
        {/* Center view */}
        {centerId && (
          <div
            style={{
              color: "grey",
              textAlign: "right",
              padding: "10px",
            }}>
            {t("currentProtocolsApplicable")}{" "}
            {centerProtocols.map((protocol) => protocol.reference).join(", ")}
          </div>
        )}

        <div style={{ width: "100%", height: "80vh", cursor: "pointer" }}>
          {materials ? (
            <LocalizedDataGrid
              autoWidth
              slots={{
                noRowsOverlay: NoRowOverlay,
                loadingOverlay: LinearProgress,
              }}
              rows={sortedMaterials}
              columns={columns}
              loading={isLoading}
              hideFooter
              checkboxSelection={true}
              // checkboxSelection={!centerUser && !centerId}
              onRowSelectionModelChange={(selection) => {
                setSelectedRows(selection);
              }}
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 100 },
                },
              }}
              pageSizeOptions={[50, 100]}
              rowHeight={30}
            />
          ) : (
            <div>Loading...</div>
          )}
        </div>

        <div>
          <EditMaterialDialog
            open={open}
            handleClose={handleClose}
            handleEdit={handleEdit}
            handleDeleteMaterial={handleDeleteMaterial}
            name={name}
            setName={setName}
            type={type}
            setType={setType}
            status={status}
            setStatus={setStatus}
            count={count}
          />
        </div>

        {/* Download materials selected table */}
        <div style={{ display: "flex", justifyContent: "space-around" }}>
          <CSVDownloader
            data={filteredMaterials}
            filename={`${studyCode}${
              centerNumber ? `_${centerNumber}` : ""
            }_materials_${getToday()}`}
            name={t("materialList")}
          />

          {/* Add Materials to Protocol Dialog */}
          {!centerId && adminUser && selectedRows.length > 0 && (
            <Button
              type="submit"
              disabled={isLoading}
              onClick={() => setShowProtocolSelector(true)}
              variant="contained"
              color="primary"
              style={{ padding: "16px 88px", margin: "16px" }}>
              {t("addToProtocol")}
            </Button>
          )}
          <ProtocolSelectorDialog
            open={showProtocolSelector}
            handleClose={() => setShowProtocolSelector(false)}
            protocols={protocols}
            selectedProtocol={selectedProtocol}
            handleProtocolSelect={handleProtocolSelect}
            handleConfirm={handleConfirm}
          />
        </div>
      </Paper>

      {adminUser && (
        <>
          <Fab
            color="primary"
            aria-label="add"
            onClick={() => {
              setEditMaterialData(null); // ensure create mode
              setVisibleCreateEdit(true);
            }}
            style={{ position: "absolute", right: SPACING, bottom: SPACING }}>
            <AddIcon />
          </Fab>

          <ModalMaterial
            visible={visibleCreateEdit}
            studyId={studyId}
            studyCode={studyCode}
            // Pass data for edit mode; null means create mode
            materialId={editMaterialData?.id || null}
            initialValues={{
              name: editMaterialData?.name || "",
              type: editMaterialData?.type || "",
              status: editMaterialData?.status || "",
              unitPrice: editMaterialData?.unitPrice ?? "",
              protocolIds: editMaterialData?.protocolIds || [],
            }}
            onClose={(refresh) => {
              setVisibleCreateEdit(false);
              setEditMaterialData(null); // reset to create mode next time
              if (refresh) {
                store.dispatch(getMaterials(studyId));
              }
            }}></ModalMaterial>
        </>
      )}
    </Grid>
  );
};
